import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Input, Select } from "barte-design-system";

import { InputNumber } from "@shared/components";
import { formatPercentage } from "@shared/helpers";

import type { FlatRateFormData, ModalEditFlatRateProps } from "./modal-edit-flat-rate.type";
import styles from "./modal-edit-flat-rate.module.css";

export const ModalEditFlatRate = ({
    isOpen,
    onClose,
    title,
    icon,
    data,
    buyerRate,
    buyerDelay,
    showRateLimit,
    showBuyerRate,
    showRateType,
    availableDelays = [],
    disableDelay,
    isLoading,
    onHandleSubmit,
}: ModalEditFlatRateProps) => {
    const { t } = useTranslation();

    const delayOptions = availableDelays.map((d) => ({ value: String(d), label: `D + ${d}` }));

    const [formData, setFormData] = useState<FlatRateFormData>(data);

    const isDelayInvalid = delayOptions.length > 0 && !availableDelays.includes(formData.delayForRedeem);

    useEffect(() => {
        if (isOpen) {
            setFormData(data);
        }
    }, [isOpen, data]);

    const handleChange = (key: keyof FlatRateFormData, value: number | string) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
    };

    const handleSave = () => {
        onHandleSubmit(formData);
    };

    const hasDiff = JSON.stringify(formData) !== JSON.stringify(data);

    const modalTitle = (
        <div className={styles["modal-header"]}>
            {icon}
            {title}
        </div>
    ) as unknown as string;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={modalTitle}
            size="lg"
            className={styles.modal}
            footer={{
                primaryButton: {
                    children: t("payfac.pageRatePlanModalSaveButton"),
                    onClick: handleSave,
                    disabled: !hasDiff || isDelayInvalid,
                    loading: isLoading,
                },
                secondaryButton: {
                    children: t("payfac.pageRatePlanModalCancelButton"),
                    variant: "outline",
                    onClick: onClose,
                    disabled: isLoading,
                },
                buttonLayout: "hug",
            }}
        >
            <div className={styles.content}>
                <div className={styles.field}>
                    {delayOptions.length > 0 ? (
                        <>
                            <Select
                                label={t("payfac.pageRatePlanModalRepassTimeLabel")}
                                options={isDelayInvalid
                                    ? [{ value: String(formData.delayForRedeem), label: `D + ${formData.delayForRedeem} (${t("payfac.pageRatesDelayInvalidLabel")})`, disabled: true }, ...delayOptions]
                                    : delayOptions
                                }
                                value={formData?.delayForRedeem != null ? String(formData.delayForRedeem) : ""}
                                onChange={(value) => handleChange("delayForRedeem", value ? Number(value) : 0)}
                                disabled={disableDelay}
                                hidePrefixIcon
                            />
                            {isDelayInvalid && (
                                <span className={styles["delay-invalid-message"]}>
                                    {t("payfac.pageRatesDelayInvalidError")}
                                </span>
                            )}
                        </>
                    ) : (
                        <InputNumber
                            label={t("payfac.pageRatePlanModalRepassTimeLabel")}
                            value={formData?.delayForRedeem}
                            onChange={(value) => handleChange("delayForRedeem", value)}
                            prefix="D + "
                            decimalPrecision={0}
                            disabled={disableDelay}
                        />
                    )}
                </div>

                <div className={styles.field}>
                    <InputNumber
                        label={t("payfac.pageRatesModalRateLabel")}
                        value={formData?.rate}
                        onChange={(value) => handleChange("rate", value)}
                        prefix={formData?.rateType === "value" ? "R$ " : undefined}
                    />
                </div>

                {showRateLimit && (
                    <div className={styles.field}>
                        <InputNumber
                            label={t("payfac.pageRatePlanModalRateLimitLabel")}
                            value={formData?.rateLimitValue}
                            onChange={(value) => handleChange("rateLimitValue", value)}
                            prefix="R$ "
                        />
                    </div>
                )}

                {showBuyerRate && (
                    <div className={styles.field}>
                        <Input
                            label={t("payfac.pageRatesModalBuyerRateLabel")}
                            value={`${formatPercentage(buyerRate)}  (D + ${buyerDelay})`}
                            onChange={() => null}
                            disabled
                        />
                    </div>
                )}

                {showRateType && (
                    <div className={styles.field}>
                        <Select
                            label={t("payfac.pageRatesBankSlipRateTypeLabel")}
                            options={[
                                { value: "percent", label: t("payfac.pageRatesBankSlipRateTypePercent") },
                                { value: "value", label: t("payfac.pageRatesBankSlipRateTypeValue") },
                            ]}
                            value={formData?.rateType ?? "percent"}
                            onChange={(value) => handleChange("rateType", value)}
                            hidePrefixIcon
                        />
                    </div>
                )}
            </div>
        </Modal>
    );
};
